import React, { useEffect, useState, useCallback } from "react";
import api, { describeError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState } from "@/components/common/EmptyState";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Sparkles, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const RZP_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = RZP_SCRIPT;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function fmtBytes(b) {
  if (b == null) return "Unlimited";
  if (b >= 1024 * 1024 * 1024) return `${(b / (1024 ** 3)).toFixed(b % (1024 ** 3) === 0 ? 0 : 1)} GB`;
  return `${Math.round(b / (1024 * 1024))} MB`;
}

function UsageRow({ label, used, limit, render }) {
  const unlimited = limit == null;
  const pct = unlimited ? 0 : Math.min(100, Math.round((used / limit) * 100));
  const over = !unlimited && used >= limit;
  return (
    <div data-testid={`usage-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="mb-1 flex items-center justify-between text-sm">
        <span className="text-navy">{label}</span>
        <span className="font-medium text-navy">
          {render ? render(used) : used} <span className="text-muted-foreground">/ {unlimited ? "Unlimited" : (render ? render(limit) : limit)}</span>
        </span>
      </div>
      {!unlimited && (
        <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
          <div className={cn("h-full rounded-full transition-all", over ? "bg-rose-500" : "bg-gold")} style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}

const STATUS_CLS = {
  trialing: "bg-blue-50 text-blue-700 border-blue-200",
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-slate-100 text-slate-600 border-slate-200",
  expired: "bg-rose-50 text-rose-700 border-rose-200",
  past_due: "bg-amber-50 text-amber-700 border-amber-200",
  suspended: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function Subscription() {
  const { checkAuth } = useAuth();
  const [data, setData] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const [busy, setBusy] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [payments, setPayments] = useState([]);

  const load = useCallback(async () => {
    const { data } = await api.get("/subscription");
    setData(data);
    if (data.billing_cycle) setCycle(data.billing_cycle);
    const p = await api.get("/subscription/payments").catch(() => ({ data: { payments: [] } }));
    setPayments(p.data.payments || []);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  if (!data) return <LoadingState />;
  const { limits, usage } = data;
  const authorized = ["authenticated", "active"].includes(data.provider_status);
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");
  const planLabel = (pl) => (pl === "law_firm" ? "Law Firm" : pl === "pro" ? "Pro Advocate" : pl === "free" ? "Free" : (pl || "—"));

  const doConfirm = async () => {
    const c = confirm; setConfirm(null);
    try {
      if (c.type === "cancel") {
        await api.post("/subscription/cancel");
        toast.success("Cancellation scheduled. Access continues until your period ends.");
      } else {
        await api.post("/subscription/downgrade", { target_plan: "pro" });
        toast.success("Downgrade scheduled for your next billing cycle.");
      }
      await checkAuth(); await load();
    } catch (err) {
      toast.error(describeError(err, "Could not update your subscription."));
    }
  };

  const priceOf = (p) => (cycle === "monthly" ? p.price_monthly : p.price_yearly);

  const upgrade = async (plan) => {
    setBusy(plan);
    try {
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load the payment window. Please try again."); return; }
      const { data: order } = await api.post("/subscription/upgrade", { target_plan: plan, billing_cycle: cycle });
      const rzp = new window.Razorpay({
        key: order.razorpay_key_id,
        subscription_id: order.subscription_id,
        name: "Advocate Diary",
        description: order.description,
        prefill: order.prefill,
        theme: { color: "#0a1f44" },
        handler: async (resp) => {
          try {
            await api.post("/subscription/verify", {
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_subscription_id: resp.razorpay_subscription_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment method authorized. Your plan is confirmed.");
            await checkAuth();
            await load();
          } catch (err) {
            toast.error(describeError(err, "We could not verify your payment. Please try again."));
          }
        },
        modal: {
          ondismiss: () => toast.message("Checkout closed. You can upgrade anytime — nothing was charged."),
        },
      });
      rzp.on("payment.failed", () => toast.error("Payment failed. Please try again."));
      rzp.open();
    } catch (err) {
      toast.error(describeError(err, "Could not start the upgrade."));
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="subscription-page">
      <PageHeader title="Subscription" subtitle="Your plan, trial status and current usage." />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="border-border p-6 shadow-sm lg:col-span-1" data-testid="current-plan-card">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-gold" />
            <h2 className="font-display text-2xl text-navy">{data.plan_name}</h2>
          </div>
          <span className={cn("mt-3 inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium capitalize", STATUS_CLS[data.subscription_status] || STATUS_CLS.active)} data-testid="subscription-status">
            {data.subscription_status.replace("_", " ")}
          </span>

          {data.subscription_status === "trialing" && (
            <div className="mt-4 rounded-md bg-navy/5 p-3 text-sm" data-testid="trial-info">
              <p className="font-medium text-navy">{data.remaining_trial_days} days left in your trial</p>
              <p className="text-xs text-muted-foreground">Trial ends {data.trial_ends_at ? new Date(data.trial_ends_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}</p>
            </div>
          )}

          {authorized && (
            <div className="mt-4 flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm" data-testid="payment-authorized-badge">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
              <div>
                <p className="font-medium text-emerald-800">Payment method authorized</p>
                <p className="text-xs text-emerald-700">Billing: {data.billing_cycle}. Razorpay status: {data.provider_status}.</p>
                {data.razorpay_subscription_id && <p className="mt-0.5 text-[11px] text-emerald-700/80">Ref: {data.razorpay_subscription_id}</p>}
              </div>
            </div>
          )}

          <div className="mt-4 space-y-1 text-sm text-muted-foreground">
            <p>Monthly: <span className="text-navy">₹{data.pricing.monthly}</span></p>
            <p>Yearly: <span className="text-navy">₹{data.pricing.yearly}</span></p>
            <p className="capitalize">Billing cycle: <span className="text-navy">{data.billing_cycle}</span></p>
            {data.current_period_end && (
              <p data-testid="period-end">
                {data.subscription_status === "trialing" ? "First billing" : (data.cancel_at_period_end ? "Access until" : "Renews on")}:{" "}
                <span className="text-navy">{fmtDate(data.current_period_end)}</span>
              </p>
            )}
          </div>

          {data.scheduled_plan && (
            <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="scheduled-change-banner">
              {data.scheduled_plan === "free"
                ? `Cancellation scheduled — your plan moves to Free on ${fmtDate(data.change_effective_at)}.`
                : `Downgrade to ${data.scheduled_plan === "pro" ? "Pro Advocate" : data.scheduled_plan} scheduled for ${fmtDate(data.change_effective_at)}.`}
            </div>
          )}

          {authorized && !data.scheduled_plan && (
            <div className="mt-5 space-y-2 border-t border-border pt-4" data-testid="manage-subscription">
              {data.plan === "law_firm" && (
                <Button variant="outline" className="w-full" data-testid="downgrade-pro-btn"
                  onClick={() => setConfirm({ type: "downgrade" })}>Downgrade to Pro</Button>
              )}
              <Button variant="ghost" className="w-full text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                data-testid="cancel-subscription-btn" onClick={() => setConfirm({ type: "cancel" })}>Cancel subscription</Button>
            </div>
          )}
        </Card>

        <Card className="border-border p-6 shadow-sm lg:col-span-2" data-testid="usage-card">
          <h3 className="mb-4 font-display text-xl text-navy">Current Usage</h3>
          <div className="space-y-5">
            <UsageRow label="Cases" used={usage.cases} limit={limits.cases} />
            <UsageRow label="Clients" used={usage.clients} limit={limits.clients} />
            <UsageRow label="Hearings this month" used={usage.hearings_this_month} limit={limits.hearings_per_month} />
            <UsageRow label="Storage" used={usage.storage_bytes} limit={limits.storage_bytes} render={fmtBytes} />
            <UsageRow label="Team members" used={usage.team_members} limit={limits.team_members} />
          </div>
        </Card>
      </div>

      <div className="mb-3 mt-8 flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-display text-2xl text-navy">Plans</h3>
        <div className="inline-flex rounded-full border border-border bg-secondary/60 p-1" data-testid="billing-cycle-toggle">
          <button onClick={() => setCycle("monthly")} data-testid="cycle-monthly"
            className={cn("rounded-full px-4 py-1.5 text-sm font-medium transition-colors", cycle === "monthly" ? "bg-navy text-white" : "text-navy")}>Monthly</button>
          <button onClick={() => setCycle("yearly")} data-testid="cycle-yearly"
            className={cn("rounded-full px-4 py-1.5 text-sm font-medium transition-colors", cycle === "yearly" ? "bg-navy text-white" : "text-navy")}>Yearly <span className="text-xs opacity-80">(save ~17%)</span></button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {data.all_plans.map((p) => {
          const isCurrent = p.id === data.plan;
          const isPaid = p.id !== "free";
          const currentAuthorized = isCurrent && authorized;
          return (
            <Card key={p.id} className={cn("flex flex-col border-border p-5 shadow-sm", isCurrent && "border-gold ring-1 ring-gold")} data-testid={`plan-${p.id}`}>
              <div className="flex items-center justify-between">
                <h4 className="font-display text-xl text-navy">{p.name}</h4>
                {isCurrent && <span className="rounded-full bg-gold/15 px-2 py-0.5 text-xs font-medium text-gold-dark">Current</span>}
              </div>
              <p className="mt-1 text-2xl font-semibold text-navy">₹{priceOf(p)}<span className="text-sm font-normal text-muted-foreground">/{cycle === "monthly" ? "mo" : "yr"}</span></p>
              <ul className="mt-4 space-y-1.5 text-sm text-muted-foreground">
                <li className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-gold" />Cases: {p.limits.cases ?? "Unlimited"}</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-gold" />Clients: {p.limits.clients ?? "Unlimited"}</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-gold" />Storage: {fmtBytes(p.limits.storage_bytes)}</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-gold" />Team: {p.limits.team_members}</li>
              </ul>
              <div className="mt-5 flex-1" />
              {isPaid && (
                currentAuthorized ? (
                  <Button disabled className="w-full bg-emerald-600/90 text-white" data-testid={`plan-${p.id}-authorized`}>Authorized</Button>
                ) : (
                  <Button onClick={() => upgrade(p.id)} disabled={busy === p.id}
                    data-testid={`upgrade-${p.id}-btn`}
                    className="w-full bg-gold text-navy hover:bg-gold-dark">
                    {busy === p.id ? "Starting…" : (isCurrent ? "Authorize payment" : `Choose ${p.name}`)}
                  </Button>
                )
              )}
              {!isPaid && <p className="text-center text-xs text-muted-foreground">Included by default</p>}
            </Card>
          );
        })}
      </div>

      <Card className="mt-8 border-border p-6 shadow-sm" data-testid="billing-history">
        <h3 className="mb-4 font-display text-xl text-navy">Billing History</h3>
        {payments.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="billing-empty">
            No payments yet. Your paid invoices will appear here once your subscription is charged.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="billing-table">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="py-2 pr-4 font-medium">Date</th>
                  <th className="py-2 pr-4 font-medium">Plan</th>
                  <th className="py-2 pr-4 font-medium">Billing Cycle</th>
                  <th className="py-2 pr-4 font-medium">Amount</th>
                  <th className="py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p, i) => (
                  <tr key={p.razorpay_payment_id || i} className="border-b border-border/60" data-testid="billing-row">
                    <td className="py-2 pr-4 text-navy">{fmtDate(p.paid_at || p.created_at)}</td>
                    <td className="py-2 pr-4 text-navy">{planLabel(p.plan)}</td>
                    <td className="py-2 pr-4 capitalize">{p.billing_cycle || "—"}</td>
                    <td className="py-2 pr-4 text-navy">₹{p.amount_inr != null ? p.amount_inr.toLocaleString("en-IN") : "—"}</td>
                    <td className="py-2">
                      <span className={cn("rounded-full px-2 py-0.5 text-xs font-medium capitalize",
                        (p.status === "captured" || p.status === "paid") ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700")}>
                        {p.status || "—"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="subscription-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.type === "cancel" ? "Cancel subscription?" : "Downgrade to Pro Advocate?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.type === "cancel"
                ? `Your ${data.plan_name} access continues until ${fmtDate(data.current_period_end)}. After that your account moves to the Free plan and you won't be charged again.`
                : `You'll keep Law Firm features until ${fmtDate(data.current_period_end)}, then move to Pro Advocate at your next billing cycle.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-no">Keep plan</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-yes" onClick={doConfirm}>
              {confirm?.type === "cancel" ? "Yes, cancel" : "Confirm downgrade"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
